<script>
  import { _ as t } from "svelte-i18n";
  import { navigate } from "svelte-routing";
  import gql from "graphql-tag";

  import { client } from "apollo.js";

  import { loginUser } from "login.js";
  import { cantons } from "cantons.js";

  import Button from "components/Button.svelte";
  import Input from "components/Input.svelte";
  import Errors from "components/GraphQLErrors.svelte";

  import {
    checkValidPassword,
    checkSamePassword,
    checkMinimumLength,
  } from "./passwordUtils.js";

  import { isPresent } from "validations.js";

  export let appLogin;

  let email;
  let password;
  let passwordConfirm;
  let firstName;
  let lastName;
  let role;
  let party;
  let canton;

  let errorMessages = [];

  const resetErrors = () => {
    errorMessages = [];
  };

  const handleRegister = ({
    email,
    password,
    firstName,
    lastName,
    role,
    party,
    canton,
  }) => {
    registerUser({ email, password, firstName, lastName, role, party, canton })
      .then((r) => {
        onRegister();
      })
      .catch((error) => {
        errorMessages = error.graphQLErrors;
        console.log(errorMessages);
      });
  };

  const REGISTER_MUTATION = gql`
    mutation(
      $email: String!
      $password: String!
      $firstName: String!
      $lastName: String!
      $function: String
      $party: String
      $canton: String
    ) {
      registerUser(
        email: $email
        password: $password
        firstName: $firstName
        lastName: $lastName
        function: $function
        party: $party
        canton: $canton
      ) {
        email
      }
    }
  `;

  const registerUser = async ({
    email,
    password,
    firstName,
    lastName,
    role,
    party,
    canton,
  }) => {
    const result = await client.mutate({
      mutation: REGISTER_MUTATION,
      variables: {
        email,
        password,
        firstName,
        lastName,
        function: role,
        party,
        canton,
      },
    });
    return result;
  };

  const onRegister = (r) => {
    loginUser(email, password)
      .then(({ data }) => {
        appLogin(data.loginUser.token);
        navigate("/");
      })
      .catch((error) => {
        errorMessages = error.graphQLErrors;
        console.log(errorMessages);
      });
  };

  $: isValidForm =
    checkValidPassword(password, passwordConfirm) &&
    email &&
    email.length > 0 &&
    isPresent(firstName) &&
    isPresent(lastName) &&
    isPresent(role);
  $: samePassword = checkSamePassword(password, passwordConfirm);
  $: minLength = checkMinimumLength(password);
</script>

<form class="form">
  <h1>{$t("register")}</h1>

  <Errors {errorMessages} />

  <div class="form-section">
    <Input
      bind:value={firstName}
      key="name"
      label={$t("registration.name")}
      required
    />
    <Input
      bind:value={lastName}
      key="surname"
      label={$t("registration.surname")}
      required
    />
    <Input
      bind:value={role}
      key="role"
      label={$t("registration.role")}
      required
    />
    <Input key="party" bind:value={party} label={$t("registration.party")} />
    <Input
      bind:value={canton}
      key="canton"
      type="select"
      options={cantons}
      label={$t("registration.canton")}
    />
  </div>

  <div class="form-section">
    <Input
      on:change={resetErrors}
      bind:value={email}
      type="email"
      key="email"
      label={$t("registration.email")}
      required
    />
    <Input
      on:change={resetErrors}
      bind:value={password}
      type="password"
      key="password"
      label={$t("registration.password")}
      required
    />
    <Input
      bind:value={passwordConfirm}
      type="password"
      key="passwordConfirm"
      label={$t("registration.passwordConfirm")}
      required
    />

    {#if !minLength}
      <p class="hint">{$t("registration.hintPassword")}</p>
    {:else}
      <p class="hint">&#8203;</p>
    {/if}

    {#if !samePassword}
      <p class="hint">{$t("registration.hintPasswordConfirmation")}</p>
    {:else}
      <p class="hint">&#8203;</p>
    {/if}
  </div>

  <div class="privacy-policy">
    <h2>{$t("privacyPolicy.title")}</h2>
    <p>{$t("privacyPolicy.text")}</p>
  </div>

  <Button
    active={isValidForm}
    on:click={() =>
      handleRegister({
        email,
        password,
        firstName,
        lastName,
        role,
        party,
        canton,
      })}
  >
    {$t("register")}
  </Button>
</form>

<style>
  .form {
    margin-top: 48px;
  }

  h1 {
    font-weight: 300;
    font-size: 32px;
    margin: 0;
  }

  .hint {
    font-size: 12px;
  }

  /* easier to style than a fieldset */
  .form-section + .form-section {
    padding-top: 24px;
  }

  .privacy-policy {
    font-size: 12px;
    margin: 24px 0 48px;
  }

  .privacy-policy h2 {
    font-weight: 300;
    font-size: 18px;
    margin: 0;
  }
</style>
